<template>
  <base-card :size="size"
             @click="openScreenTimeModal">
    <div slot="title">
      {{ $t("screentimeManagement.cardTitle") }}

      <span v-if="!screentimeLeft"
            class="exhausted-warning">
        <icon-base class="icon"
                   width="12"
                   height="12"
                   view-box="0 0 15 15"
                   icon-color="#f35d5d">
            <icon-attention/>
        </icon-base>

        {{ $t("screentimeManagement.exhausted") }}
      </span>
    </div>

    <div slot="content"
         class="screen-time"
         :class="classes"
         v-if="devices && devices.length">
      <div class="time">
        <div class="time__label">
          {{ $t("used") }}
        </div>

        <div class="time__value">
          <div class="time__hours">
            {{ screenTime.used.hours }}<span class="time__unit">h</span>
          </div>

          <div class="time__minutes">
            {{ screenTime.used.minutes }}<span class="time__unit">m</span>
          </div>
        </div>
      </div>

      <div class="time"
            :class="screenTimeExhaustedClasses">
        <div class="time__label">
          {{ $t("left") }}
        </div>

        <div class="time__value">
          <template v-if="!isScreenTimeUnlimited">
            <div class="time__hours">
              {{ screenTime.left.hours }}<span class="time__unit">h</span>
            </div>

            <div class="time__minutes">
              {{ screenTime.left.minutes }}<span class="time__unit">m</span>
            </div>
          </template>

          <div v-else class="time__unlimited">
            {{ $t("screentimeManagement.unlimited") }}
          </div>
        </div>
      </div>
    </div>

    <div v-else slot="content" class="no-device">
      {{ $t("noDevices") }}
    </div>
  </base-card>
</template>

<script>
  import { getTimeString } from '@/services/utils.js'
  import * as intl from '@/services/intl'
  import analytics from '@/services/analytics.js'

  export default {
    props: {
      childId: {
        required: true,
        type: String
      },

      size: {
        required: false,
        type: String
      }
    },

    computed: {
      ...mapGetters('children', {
        getChildById: 'getById'
      }),

      child() {
        return this.getChildById(this.childId)
      },

      screenTime() {
        let usedScreenTime = 0
        if (
          this.child && this.child.latest_accumulated_screentime &&
          intl.isToday(this.child.latest_accumulated_screentime.date)
        ) {
          usedScreenTime = Math.floor(this.child.latest_accumulated_screentime.sum_in_seconds / 60)
        }

        let usedScreenTimeObject = this.getScreenTimeObject(usedScreenTime)
        let leftScreenTime = usedScreenTime < this.screenTimeLimit ? this.screenTimeLimit - usedScreenTime : 0
        let leftScreenTimeObject = this.getScreenTimeObject(leftScreenTime)

        return {
          used: {
            hours: usedScreenTimeObject.hours,
            minutes: usedScreenTimeObject.minutes
          },

          left: {
            hours: leftScreenTimeObject.hours,
            minutes: leftScreenTimeObject.minutes
          }
        }
      },

      screenTimeLimit() {
        if (intl.isToday(this.child.settings.screen_time_override_date)) {
          return this.child.settings.screen_time_override_in_minutes
        }

        return this.child.settings.screen_time_limit_in_minutes[intl.getDayOfTheWeek()]
      },

      isScreenTimeUnlimited() {
        return this.screenTimeLimit === 1440
      },

      screentimeLeft() {
        if (!this.child || !this.child.latest_accumulated_screentime) {
          return true
        }

        let usedScreenTime = Math.floor(this.child.latest_accumulated_screentime.sum_in_seconds / 60)

        return usedScreenTime < this.screenTimeLimit
      },

      screenTimeExhaustedClasses() {
        return {
          'exhausted-time': !this.screentimeLeft
        }
      },

      devices() {
        return this.child.devices
      },

      classes() {
        return [
          `screen-time-${this.size}`
        ]
      },

      ...mapGetters('auth', {
        currentUser: 'currentUser'
      })
    },

    methods: {
      getScreenTimeObject(screenTime) {
        let screenTimeHours = Math.floor(screenTime / 60)
        let screenTimeMinutes = screenTime % 60

        return {
          hours: getTimeString(screenTimeHours),
          minutes: getTimeString(screenTimeMinutes)
        }
      },

      openScreenTimeModal() {
        analytics.sendEvent('Pk_ScrTime')

        if (!this.currentUser.hasPaidLicense || !this.devices.length) {
          return
        }

        if (this.$mq === 'mobile') {
          this.$router.push({
            name: 'children.single.screenTimeManagement',
            params: {
              childId: this.child.id
            }
          })

          return
        }

        this.showModal('screentime', {
          child: this.child
        })
      }
    },
  }
</script>

<style lang="scss" scoped>
  .screen-time {
    width: 100%;

    display: flex;
    justify-content: space-between;

    .time {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      line-height: 1;

      &__label {
        font-size: 16px;
        font-weight: bold;
        color: map.get($colors, disabled);
      }

      &__value {
        display: flex;
        font-size: 40px;
        color: map.get($colors, text);
      }

      &__hours {
        margin-right: 4px;
      }

      &__unit {
        font-size: 22px;
        font-weight: bold;
      }

      &__unlimited {
        font-size: 36px;
      }
    }

    &-mobile {
      .time {
        &__label {
          font-size: 16px;
        }

        &__value {
          font-size: 28px;
        }

        &__hours {
          margin-right: 4px;
        }

        &__unit {
          font-size: 14px;
        }

        &__unlimited {
          font-size: 22px;
        }
      }
    }
  }

  .exhausted-warning {
    color: map.get($colors, error);
  }

  .no-device {
    font-size: 18px;
    font-weight: bold;
    color: map.get($colors, disabled);
  }
</style>